/**
 * "Get involved" — three routes to participate (give, volunteer, partner), each an
 * image-backed card with a clear action. Volunteer/partner point at the enquiry form;
 * giving points at the donate flow.
 */
import { HandHeart, Users, Building2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Figure } from '../components/ui/Figure.jsx';
import { useReveal } from '../hooks/useReveal.js';
import { MEDIA } from '../data/media.js';
import styles from './GetInvolved.module.css';

const WAYS = [
  {
    icon: HandHeart,
    img: MEDIA.hopeHands,
    title: 'Give',
    body: 'Fund a verified case directly. Every rupee is tracked end-to-end and shows up in the live transparency report.',
    action: 'Donate now',
    to: '/donate',
  },
  {
    icon: Users,
    img: MEDIA.volunteers,
    title: 'Volunteer',
    body: 'Offer time or a skill — field verification, documentation, design, outreach. Tell us where you can help.',
    action: 'Join as a volunteer',
    to: '/contact?topic=volunteer',
  },
  {
    icon: Building2,
    img: MEDIA.handsTogether,
    title: 'Partner / CSR',
    body: 'Companies and foundations can route CSR funding to audited, reportable outcomes with full documentation.',
    action: 'Start a partnership',
    to: '/contact?topic=csr',
  },
];

export function GetInvolved() {
  const ref = useReveal({ threshold: 0.1 });
  return (
    <section id="get-involved" className={styles.section}>
      <div className="container">
        <div className={styles.head}>
          <p className="eyebrow">Get involved</p>
          <h2 className={styles.title}>Three ways to stand behind the work.</h2>
          <p className={styles.lede}>
            Whether you give once, give time, or bring an organisation along, the standard is the
            same — your contribution lands on a verified case and shows up in the public record.
          </p>
        </div>

        <div ref={ref} className={styles.grid} data-revealed="false">
          {WAYS.map((w, i) => (
            <article key={w.title} className={styles.card} style={{ '--i': i }}>
              <div className={styles.media}>
                <Figure id={w.img} alt={w.title} ratio="16 / 11" w={620} />
                <span className={styles.icon}>
                  <w.icon size={22} aria-hidden="true" />
                </span>
              </div>
              <div className={styles.body}>
                <h3 className={styles.cardTitle}>{w.title}</h3>
                <p className={styles.cardCopy}>{w.body}</p>
                <Link to={w.to} className={styles.action}>
                  {w.action} <ArrowRight size={18} aria-hidden="true" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default GetInvolved;
